import { Maybe, RestOptions } from '@csp/csp-common-model';
import { Page } from '@csp/dmdp-api-common-dto';
import type {
  StudyDocsTaxonInV1,
  StudyDocsTaxonV1,
  StudyDocsTaxonomyV1,
  StudyFileInV1,
  StudyFileQueryV1,
  StudyFileSetClassificationInV1,
  StudyFileSetDomainsInV1,
  StudyFileSetOrgIdsInV1,
  StudyFileSetRolesInV1,
  StudyFileV1,
  StudyFilesV1,
  TaxonNameInV1,
} from '@csp/dmdp-api-file-storage-studydocs-dto';
import { fileStorageUrl } from '../../dmdp-config/dmdpConfig';
import { BinaryFileData } from '../model/BinaryFileData';

const studyDocsUrl = (): string => `${fileStorageUrl()}/studydocs/v1`;
const studyFilesUrl = (): string => `${studyDocsUrl()}/study-files`;
const taxonomyUrl = (): string => `${studyDocsUrl()}/taxonomy`;

const queryStudyFilesUrl = (): string => `${studyFilesUrl()}:query`;
const queryStudyFiles = async (
  { axios, signal }: RestOptions,
  query: StudyFileQueryV1,
  page?: Page,
): Promise<StudyFilesV1> => {
  const { data } = await axios.post<StudyFilesV1>(queryStudyFilesUrl(), query, { params: page, signal });
  return data;
};

const getStudyFileUrl = (fileId: string): string => `${studyFilesUrl()}/${fileId}`;
const getStudyFile = async ({ axios, signal }: RestOptions, fileId: string): Promise<StudyFileV1> => {
  const { data } = await axios.get<StudyFileV1>(getStudyFileUrl(fileId), { signal });
  return data;
};

const getTaxonomyUrl = taxonomyUrl;
const getTaxonomy = async ({ axios, signal }: RestOptions): Promise<StudyDocsTaxonomyV1> => {
  const { data } = await axios.get<StudyDocsTaxonomyV1>(getTaxonomyUrl(), { signal });
  return data;
};

const getTaxonUrl = (taxonId: string): string => `${getTaxonomyUrl()}/taxa/${taxonId}`;
const getTaxon = async ({ axios, signal }: RestOptions, taxonId: string): Promise<StudyDocsTaxonV1> => {
  const { data } = await axios.get<StudyDocsTaxonV1>(getTaxonUrl(taxonId), { signal });
  return data;
};

const putFileTrashStatusUrl = (fileId: string): string => `${studyFilesUrl()}/${fileId}/trash`;
const putFileTrashStatus = async (
  { axios, signal }: RestOptions,
  fileId: string,
  trashStatus: boolean,
): Promise<StudyFileV1> => {
  const { data } = await axios.put<StudyFileV1>(putFileTrashStatusUrl(fileId), { trash: trashStatus }, { signal });
  return data;
};

const getCreateTaxonomyUrl = (): string => `${getTaxonomyUrl()}/taxa`;
const createTaxonomy = async (
  { axios, signal }: RestOptions,
  studyDocsTaxonInV1: StudyDocsTaxonInV1,
): Promise<StudyDocsTaxonV1> => {
  const { data } = await axios.post<StudyDocsTaxonV1>(getCreateTaxonomyUrl(), studyDocsTaxonInV1, { signal });
  return data;
};

const deleteFileUrl = (fileId: string): string => `${studyFilesUrl()}/${fileId}`;
const deleteFile = async ({ axios, signal }: RestOptions, fileId: string): Promise<void> => {
  await axios.delete(deleteFileUrl(fileId), { signal });
};

const createStudyFile = async (
  { axios, signal }: RestOptions,
  fields: StudyFileInV1,
): Promise<{ signedUrl: Maybe<string>; data: StudyFileV1 }> => {
  const { headers, data } = await axios.post<StudyFileV1>(studyFilesUrl(), { ...fields }, { signal });

  return { signedUrl: headers['location'], data };
};
const uploadStudyFile = async (
  { axios, signal }: RestOptions,
  data: { file: unknown; mimeType: string; signedUrl: string },
): Promise<void> => {
  const headers = { 'Content-Type': data.mimeType };
  return await axios.put(data.signedUrl, data.file, { signal, headers });
};

const editClassificationUrl = (fileId: string): string => `${getStudyFileUrl(fileId)}/classification`;
const editClassification = async (
  { axios, signal }: RestOptions,
  { fileId, ...args }: StudyFileSetClassificationInV1 & { fileId: string },
): Promise<StudyFileV1> => {
  const { data } = await axios.put<StudyFileV1>(editClassificationUrl(fileId), { ...args }, { signal });
  return data;
};

const editDomainsUrl = (fileId: string): string => `${getStudyFileUrl(fileId)}/domains`;
const editDomains = async (
  { axios, signal }: RestOptions,
  { fileId, ...args }: StudyFileSetDomainsInV1 & { fileId: string },
): Promise<StudyFileV1> => {
  const { data } = await axios.put<StudyFileV1>(editDomainsUrl(fileId), { ...args }, { signal });
  return data;
};
const editOrgIdsUrl = (fileId: string): string => `${getStudyFileUrl(fileId)}/org-ids`;
const editOrgIds = async (
  { axios, signal }: RestOptions,
  { fileId, ...args }: StudyFileSetOrgIdsInV1 & { fileId: string },
): Promise<StudyFileV1> => {
  const { data } = await axios.put<StudyFileV1>(editOrgIdsUrl(fileId), { ...args }, { signal });
  return data;
};
const editRolesUrl = (fileId: string): string => `${getStudyFileUrl(fileId)}/roles`;
const editRoles = async (
  { axios, signal }: RestOptions,
  { fileId, ...args }: StudyFileSetRolesInV1 & { fileId: string },
): Promise<StudyFileV1> => {
  const { data } = await axios.put<StudyFileV1>(editRolesUrl(fileId), { ...args }, { signal });
  return data;
};

const editTaxonomyUrl = (taxonId: string): string => `${getTaxonomyUrl()}/taxa/${taxonId}/taxon`;
const editTaxonomy = async (
  { axios, signal }: RestOptions,
  taxonId: string,
  taxonNameInV1: TaxonNameInV1,
): Promise<StudyDocsTaxonV1> => {
  const { data } = await axios.put<StudyDocsTaxonV1>(editTaxonomyUrl(taxonId), taxonNameInV1, {
    signal,
  });
  return data;
};

const getBinaryFileDataUrl = (fileId: string): string => `${studyFilesUrl()}/${fileId}/file-data`;
const getBinaryFileData = async ({ signal, axios }: RestOptions, fileId: string): Promise<BinaryFileData> => {
  const response = await axios.get(getBinaryFileDataUrl(fileId), {
    signal,
    responseType: 'blob',
  });
  return {
    blob: response.data as Blob,
    contentType: (response.headers['Content-Type'] ?? response.headers['content-type'] ?? 'text/plain') as string,
  };
};

const getFileUrl = async ({ signal, axios }: RestOptions, fileId: string): Promise<string> => {
  const response = await axios.get(getBinaryFileDataUrl(fileId), {
    signal,
    responseType: 'blob',
  });
  return response.request?.responseURL;
};

const deleteTaxonomyUrl = (taxonId: string): string => `${getTaxonomyUrl()}/taxa/${taxonId}`;
const deleteTaxonomy = async ({ axios, signal }: RestOptions, taxonId: string): Promise<void> => {
  await axios.delete<StudyDocsTaxonV1>(deleteTaxonomyUrl(taxonId), { signal });
};

export const StudyDocsRestServiceV1 = {
  queryStudyFiles,
  queryStudyFilesUrl,
  getStudyFileUrl,
  getStudyFile,
  getTaxonomy,
  getTaxonomyUrl,
  getTaxon,
  getTaxonUrl,
  putFileTrashStatusUrl,
  putFileTrashStatus,
  getCreateTaxonomyUrl,
  createTaxonomy,
  deleteFileUrl,
  deleteFile,
  studyFilesUrl,
  createStudyFile,
  uploadStudyFile,
  editTaxonomyUrl,
  editTaxonomy,
  getBinaryFileDataUrl,
  getBinaryFileData,
  getFileUrl,
  deleteTaxonomy,
  deleteTaxonomyUrl,
  editDomains,
  editDomainsUrl,
  editClassification,
  editClassificationUrl,
  editOrgIds,
  editOrgIdsUrl,
  editRoles,
  editRolesUrl,
};
